/*
 * iam's rule for an organization name (dodil-iam org_name_value_objects.rs),
 * checked here as it is typed so nobody learns it from a refusal: lowercase
 * letters, digits and single hyphens between them, at least one letter, 2 or
 * more characters, and at most 20 counting each hyphen twice (Ignite writes
 * the org into hostnames with every `-` doubled).
 */

export const ORG_NAME_MAX = 20;
const ORG_NAME_MIN = 2;

/** The length iam counts: characters, plus one more per hyphen. */
export const countedLength = (name: string) => name.length + (name.match(/-/g)?.length ?? 0);

/** Why iam would refuse it, in words; undefined when it's fine. */
export function orgNameProblem(name: string): string | undefined {
    if (name.length < ORG_NAME_MIN) return 'Use at least 2 characters.';
    if (/[^a-z0-9-]/.test(name)) return 'Use lowercase letters, digits and hyphens only.';
    if (name.startsWith('-') || name.endsWith('-')) return 'Start and end with a letter or digit.';
    if (name.includes('--')) return 'Use one hyphen at a time.';
    if (!/[a-z]/.test(name)) return 'Include at least one letter.';
    if (countedLength(name) > ORG_NAME_MAX) return `Too long: at most ${ORG_NAME_MAX}, and each hyphen counts as two.`;
    return undefined;
}

/**
 * What someone types, as a name iam accepts the shape of: accents dropped
 * (Café → cafe), anything else becomes a hyphen, hyphens single and not at
 * the ends. Length is left to the counter.
 */
export function toOrgName(typed: string): string {
    return typed
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        // "Ana's org" is anas-org, not ana-s-org.
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-/, '');
}

/**
 * A handle for an organization's name, inside iam's budget: shaped as iam
 * accepts, then hyphens dropped from the end first (each costs two), then cut.
 * A name with no letters gets one ("2026" → "org2026").
 */
export function fitHandle(name: string): string {
    let h = toOrgName(name).replace(/-+$/, '');
    if (h && !/[a-z]/.test(h)) h = `org${h.replace(/-/g, '')}`;
    while (countedLength(h) > ORG_NAME_MAX && h.includes('-')) {
        const i = h.lastIndexOf('-');
        h = h.slice(0, i) + h.slice(i + 1);
    }
    return h.slice(0, ORG_NAME_MAX).replace(/-+$/, '');
}

/** Another handle when one is taken: a digit on the end, still inside the budget. */
export function alternativeHandle(handle: string, n = 2): string {
    const tail = String(n);
    let base = handle;
    while (countedLength(base) + tail.length > ORG_NAME_MAX) base = base.slice(0, -1).replace(/-+$/, '');
    return `${base}${tail}`;
}

const PERSONAL_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'ymail.com',
    'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me', 'protonmail.com', 'pm.me', 'gmx.com', 'gmx.de',
    'web.de', 'mail.com', 'zoho.com', 'yandex.com', 'yandex.ru', 'fastmail.com', 'hey.com', 'qq.com', '163.com',
]);

const titleCase = (s: string) => s.replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, c: string) => sep + c.toUpperCase());

/**
 * A first guess at the organization's name, from who is signing up: a work
 * address names the company (ana@acme-labs.com → "Acme Labs"), a personal one
 * names the person ("Ana's org").
 */
export function suggestOrgName(email?: string, name?: string): string {
    const [local = '', domain = ''] = (email ?? '').toLowerCase().split('@');
    if (domain && !PERSONAL_DOMAINS.has(domain)) {
        const label = domain.split('.').slice(0, -1).pop() ?? '';
        if (label && label !== 'example') return titleCase(label.replace(/[-_]+/g, ' '));
    }
    const first = (name?.trim().split(/\s+/)[0]) || local.split(/[^\p{L}]/u)[0] || '';
    return first ? `${titleCase(first)}'s org` : '';
}
